import { CookieKey, DEFAULT_EXPIRE_DAYS } from '@/constants';
import { getUserInfo } from '@/fetchers';
import type { ServerCookieManager } from '@/utils/serverCookies.utils';
import { getRefreshCookieExpiry } from './refreshCookieExpiry';
import { refreshToken as tokenRefresher } from './tokenRefresher';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

type ServerUserInfoResult = {
  userInfo: Awaited<ReturnType<typeof getUserInfo>> | null;
  /** The session could not be renewed on the server; the browser takes over (see `handOverPendingRenewal`) */
  isRenewalPending: boolean;
};

/**
 * Resolve the signed-in user during server-side rendering, renewing the access token when it has expired.
 *
 * - A rotated refresh token is stored with the expiry of its own `exp` claim; without one the refresh cookie is left alone
 * - A renewal that fails (rejected or not) deletes no cookie: the page renders without the user and is flagged
 *   `isRenewalPending`, so the browser renews under its own rules. No token is returned to the caller.
 *
 * Cookie dates use `toUTCString()`: the `Expires` attribute needs the RFC 6265 format.
 */
export const getServerUserInfo = async (cookieManager: ServerCookieManager): Promise<ServerUserInfoResult> => {
  const accessToken = cookieManager.getCookie(CookieKey.AccessToken);
  const refreshToken = cookieManager.getCookie(CookieKey.RefreshToken);
  const userId = cookieManager.getCookie(CookieKey.UserId);

  // This is to force user logout for the case user has logged in before Phoenix 04 is deployed
  if (userId && !accessToken) {
    console.info('GetUserInfo: No access token found, remove user cookies');
    cookieManager.removeUserCookies();
    return { userInfo: null, isRenewalPending: false };
  }

  if (!accessToken) return { userInfo: null, isRenewalPending: false };

  try {
    return { userInfo: await getUserInfo({ accessToken }), isRenewalPending: false };
    // biome-ignore lint/suspicious/noExplicitAny: catch clause
  } catch (error: any) {
    if (error?.status !== 401) return { userInfo: null, isRenewalPending: false };

    if (!refreshToken) {
      console.info('GetUserInfo: No refresh token found, remove user cookies');
      cookieManager.removeUserCookies();
      return { userInfo: null, isRenewalPending: false };
    }

    let renewed: Awaited<ReturnType<typeof tokenRefresher>>;
    try {
      renewed = await tokenRefresher(refreshToken);
    } catch (_refreshError) {
      return { userInfo: null, isRenewalPending: true };
    }

    cookieManager.setCookie(CookieKey.AccessToken, renewed.access_token, {
      expires: new Date(Date.now() + DEFAULT_EXPIRE_DAYS * MS_PER_DAY).toUTCString(),
    });
    if (renewed.refresh_token) {
      cookieManager.setCookie(CookieKey.RefreshToken, renewed.refresh_token, {
        expires: getRefreshCookieExpiry(renewed.refresh_token).toUTCString(),
      });
    }

    try {
      // Retry getUserInfo with new access token
      return { userInfo: await getUserInfo({ accessToken: renewed.access_token }), isRenewalPending: false };
    } catch (_retryError) {
      return { userInfo: null, isRenewalPending: false };
    }
  }
};
